/**
 * 认证与用户 API
 */

import { tokenManager } from '@/utils/token'
import { getDeviceId } from '@/utils/device'
import { API_BASE_URL } from '@/constants/api'

export interface LoginRequest {
  email: string
  password: string
  remember?: boolean
  deviceId?: string
}

export interface RegisterRequest {
  email: string
  username?: string
  password: string
  verificationCode: string
}

export interface SendVerificationCodeRequest {
  email: string
}

export interface AuthResponse {
  user: {
    id: string
    email: string
    username: string
    role: string
    isActive: boolean
    emailVerified: boolean
  }
  accessToken: string
  refreshToken?: string
  expiresAt: string
}

export interface ErrorResponse {
  error: string
  message?: string
}

export async function login(data: LoginRequest): Promise<AuthResponse> {
  const loginData = {
    ...data,
    deviceId: data.deviceId || getDeviceId()
  }
  
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(loginData),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '登录失败')
  }

  const result = await response.json()
  
  if (result.accessToken) {
    tokenManager.saveToken(
      result.accessToken, 
      result.expiresAt,
      result.refreshToken
    )
    localStorage.setItem('user', JSON.stringify(result.user))
  }
  
  return result
}

export async function sendVerificationCode(data: SendVerificationCodeRequest): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/send-verification-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '发送验证码失败')
  }
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '注册失败')
  }

  const result = await response.json()
  
  if (result.accessToken) {
    tokenManager.saveToken(
      result.accessToken, 
      result.expiresAt,
      result.refreshToken
    )
    localStorage.setItem('user', JSON.stringify(result.user))
  }
  
  return result
}

export async function logout(): Promise<void> {
  const token = localStorage.getItem('accessToken')
  
  if (token) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }
  
  tokenManager.clearToken()
}

export async function getCurrentUser(): Promise<any> {
  const token = tokenManager.getToken()
  
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('获取用户信息失败')
  }

  return response.json()
}

export interface SendChangeEmailCodeRequest {
  newEmail: string
}

export interface ChangeEmailRequest {
  newEmail: string
  code: string
}

export async function sendChangeEmailCode(data: SendChangeEmailCodeRequest): Promise<void> {
  const token = tokenManager.getToken()
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/auth/send-change-email-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '发送验证码失败')
  }
}

export async function changeEmail(data: ChangeEmailRequest): Promise<void> {
  const token = tokenManager.getToken()
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/auth/change-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '更换邮箱失败')
  }
}

export function isLoggedIn(): boolean {
  return !!localStorage.getItem('accessToken')
}

export function getStoredUser(): any {
  const userStr = localStorage.getItem('user')
  return userStr ? JSON.parse(userStr) : null
}

export interface SendPasswordResetCodeRequest {
  email: string
}

export interface ResetPasswordRequest {
  email: string
  code: string
  newPassword: string
}

export async function sendPasswordResetCode(data: SendPasswordResetCodeRequest): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/send-password-reset-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '发送验证码失败')
  }
}

export async function resetPassword(data: ResetPasswordRequest): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error: ErrorResponse = await response.json()
    throw new Error(error.error || error.message || '重置密码失败')
  }
}
